import React, { useEffect, useState } from 'react';
import { UserDataForm } from '../../../../app/models/User';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { Group } from '../../../../app/models/Group';
import ButtonGeneral from '../../../../components/buttons/ButtonGeneral';
import InputField from '../../../../components/InputField/InputField';
import TextAreaField from '../../../../components/TextAreaField';
import defaultAvatar from '../../../../assets/images/avatar-default.svg';
import useRut from '../../../../hooks/useRut';

interface GeneralUserProps {
    userForm: UserDataForm;
    setUserForm: (user: UserDataForm) => void;
    groups?: Group[];
    errorFields?: any;
    isCreate?: boolean;
    isProfile?: boolean;
}

const UserForm = ({
    userForm,
    setUserForm,
    groups,
    errorFields,
    isCreate = false,
    isProfile = false
}: GeneralUserProps) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();
    const { formatRut } = useRut();
  
    const [image, setImage] = useState(
        userForm.avatar
            ? userForm.avatar
            : defaultAvatar
    );
    
    // Verificar si el usuario tiene rol de abogado
    const isLawyer = (): boolean => {
        const roles = userForm.roles || userForm.groups || [];
        // También el administrador del estudio: firma poderes como mandatario y necesita
        // su domicilio profesional. Los roles propios del estudio no se asignan desde
        // aquí (los maneja el estudio), y el asistente no lleva casos.
        return roles.some((role: Group) =>
            role.name?.toLowerCase().includes('abogado') ||
            role.name?.toLowerCase().includes('lawyer') ||
            role.name === 'Administrador de Estudio'
        );
    };
    
    // Handler para RUT con formateo
    const handleRutChange = (value: string) => {
        const formattedRut = formatRut(value);
        setUserForm({ ...userForm, rut: formattedRut });
        onFocusRemove('rut');
    };

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    const dataOnChangeImages = (e: any) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setImage(URL.createObjectURL(selectedFile));
            setUserForm({
                ...userForm,
                [e.target.name]: selectedFile
            });
        }
    };

    const removeImage = () => {
        setImage(
            defaultAvatar
        );
        setUserForm({
            ...userForm,
            avatar: undefined
        });
        const fileInput = document.getElementById('avatar') as HTMLInputElement | null;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    if (!userForm) return null;

    // Estilos para las secciones
    const sectionStyle = {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        border: '1px solid #e9ecef'
    };

    const sectionHeaderStyle = {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1.25rem',
        paddingBottom: '0.75rem',
        borderBottom: '2px solid #e9ecef'
    };

    const iconContainerStyle = {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '12px'
    };

    return (
        <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '16px' }}>
            {/* Sección de Avatar */}
            <div style={sectionStyle}>
                <div className="text-center">
                    <div className="position-relative d-inline-block mb-3">
                        <label htmlFor="avatar" className="cursor-pointer">
                            <img
                                src={
                                    typeof image === 'string' && image !== ''
                                        ? image
                                        : window.URL.createObjectURL(image as Blob)
                                }
                                className="rounded-circle"
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    objectFit: 'cover',
                                    border: '4px solid #e9ecef',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                alt="Avatar"
                            />
                            <div 
                                className="position-absolute bottom-0 end-0 bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: '32px', height: '32px', cursor: 'pointer' }}
                            >
                                <i className="fas fa-camera text-white" style={{ fontSize: '12px' }}></i>
                            </div>
                        </label>
                        <input
                            type="file"
                            className="d-none"
                            id="avatar"
                            name="avatar"
                            accept="image/*"
                            onChange={dataOnChangeImages}
                        />
                    </div>
                    {userForm.avatar && (
                        <div className="mt-2">
                            <ButtonGeneral
                                onClick={removeImage}
                                title="Quitar foto"
                                variant="danger"
                                size="small"
                                icon="fa-trash"
                            />
                        </div>
                    )}
                    {fieldErrorMessage('avatar') && (
                        <div className="text-danger small mt-2">{fieldErrorMessage('avatar')}</div>
                    )}
                </div>
            </div>

            {/* Sección de Información Personal */}
            <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                    <div style={{ ...iconContainerStyle, backgroundColor: '#e3f2fd' }}>
                        <i className="fas fa-user" style={{ color: '#1976d2', fontSize: '16px' }}></i>
                    </div>
                    <div>
                        <h6 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Información Personal</h6>
                        <small className="text-muted">Datos básicos de tu cuenta</small>
                    </div>
                </div>
                
                <div className="row g-3">
                    <div className="col-md-4">
                        <InputField
                            type="text"
                            label="Nombre"
                            value={userForm.first_name || ''}
                            onChange={(value) => {
                                setUserForm({ ...userForm, first_name: value });
                                onFocusRemove('first_name');
                            }}
                            onFocus={() => onFocusRemove('first_name')}
                            placeholder="Ingrese el nombre"
                            error={fieldErrorMessage('first_name')}
                            required
                        />
                    </div>
                    <div className="col-md-4">
                        <InputField
                            type="text"
                            label="Apellido Paterno"
                            value={userForm.last_name || ''}
                            onChange={(value) => {
                                setUserForm({ ...userForm, last_name: value });
                                onFocusRemove('last_name');
                            }}
                            onFocus={() => onFocusRemove('last_name')}
                            placeholder="Ingrese el apellido paterno"
                            error={fieldErrorMessage('last_name')}
                            required
                        />
                    </div>
                    <div className="col-md-4">
                        <InputField
                            type="text"
                            label="Apellido Materno"
                            value={userForm.second_last_name || ''}
                            onChange={(value) => {
                                setUserForm({ ...userForm, second_last_name: value });
                                onFocusRemove('second_last_name');
                            }}
                            onFocus={() => onFocusRemove('second_last_name')}
                            placeholder="Ingrese el apellido materno"
                            error={fieldErrorMessage('second_last_name')}
                        />
                    </div>
                    <div className="col-md-6">
                        <InputField
                            type="email"
                            label="Correo electrónico"
                            value={userForm.email || ''}
                            onChange={(value) => {
                                setUserForm({ ...userForm, email: value });
                                onFocusRemove('email');
                            }}
                            onFocus={() => onFocusRemove('email')}
                            placeholder="ejemplo@correo.com"
                            error={fieldErrorMessage('email')}
                            required
                        />
                    </div>
                    <div className="col-md-6">
                        <InputField
                            type="text"
                            label="RUT"
                            value={userForm.rut || ''}
                            onChange={handleRutChange}
                            onFocus={() => onFocusRemove('rut')}
                            placeholder="Ej: 12.345.678-9"
                            error={fieldErrorMessage('rut')}
                        />
                    </div>
                </div>

                {isCreate && (
                    <div className="row g-3 mt-1">
                        <div className="col-md-12">
                            <InputField
                                type="password"
                                label="Contraseña"
                                value={userForm.password || ''}
                                onChange={(value) => {
                                    setUserForm({ ...userForm, password: value });
                                    onFocusRemove('password');
                                }}
                                onFocus={() => onFocusRemove('password')}
                                placeholder="Ingrese la contraseña"
                                error={fieldErrorMessage('password')}
                                required
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Sección de Contacto y Dirección */}
            <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                    <div style={{ ...iconContainerStyle, backgroundColor: '#e8f5e9' }}>
                        <i className="fas fa-map-marker-alt" style={{ color: '#388e3c', fontSize: '16px' }}></i>
                    </div>
                    <div>
                        <h6 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Contacto y Dirección</h6>
                        <small className="text-muted">Información de contacto y ubicación</small>
                    </div>
                </div>
                
                <div className="row g-3">
                    <div className="col-md-6">
                        <InputField
                            type="tel"
                            label="Teléfono"
                            value={userForm.phone || ''}
                            onChange={(value) => {
                                setUserForm({ ...userForm, phone: value });
                                onFocusRemove('phone');
                            }}
                            onFocus={() => onFocusRemove('phone')}
                            placeholder="Ej: +56 9 1234 5678"
                            error={fieldErrorMessage('phone')}
                        />
                    </div>
                    <div className="col-md-6">
                        <InputField
                            type="text"
                            label="Nacionalidad"
                            value={userForm.nationality || ''}
                            onChange={(value) => {
                                setUserForm({ ...userForm, nationality: value });
                                onFocusRemove('nationality');
                            }}
                            onFocus={() => onFocusRemove('nationality')}
                            placeholder="Ej: Chilena"
                            error={fieldErrorMessage('nationality')}
                        />
                    </div>
                    <div className="col-md-12">
                        <InputField
                            type="text"
                            label="Dirección"
                            value={userForm.address || ''}
                            onChange={(value) => {
                                setUserForm({ ...userForm, address: value });
                                onFocusRemove('address');
                            }}
                            onFocus={() => onFocusRemove('address')}
                            placeholder="Ej: Av. Providencia 1234, Oficina 567, Providencia, Santiago"
                            error={fieldErrorMessage('address')}
                        />
                    </div>
                </div>
            </div>

            {/* Sección de Roles */}
            <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                    <div style={{ ...iconContainerStyle, backgroundColor: '#fff3e0' }}>
                        <i className="fas fa-shield-alt" style={{ color: '#f57c00', fontSize: '16px' }}></i>
                    </div>
                    <div>
                        <h6 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Roles y Permisos</h6>
                        <small className="text-muted">Nivel de acceso en el sistema</small>
                    </div>
                </div>
                
                {isProfile ? (
                    <div className="d-flex flex-wrap gap-2">
                        {userForm.roles && userForm.roles.length > 0 ? (
                            userForm.roles.map((rol: Group) => (
                                <span 
                                    key={rol.id} 
                                    className="badge"
                                    style={{
                                        backgroundColor: '#e3f2fd',
                                        color: '#1976d2',
                                        padding: '8px 16px',
                                        borderRadius: '20px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500'
                                    }}
                                >
                                    <i className="fas fa-check-circle me-1"></i>
                                    {rol.name}
                                </span>
                            ))
                        ) : (
                            <span className="text-muted">
                                <i className="fas fa-info-circle me-1"></i>
                                No tiene roles asignados
                            </span>
                        )}
                    </div>
                ) : (
                    <SelectTwo
                        name="groups"
                        id="groups"
                        label="Seleccionar roles"
                        inputValue={userForm.groups}
                        options={SelectTwoMapperOptions(groups!)}
                        hasError={fieldHasError('groups') !== ''}
                        onChange={(value: any) =>
                            setUserForm({
                                ...userForm,
                                groups: value
                            })
                        }
                        isMulti={true}
                        placeholder="Seleccione los roles del usuario"
                        onFocus={() => onFocusRemove('groups')}
                        size="medium"
                    />
                )}
                {fieldErrorMessage('groups') && (
                    <div className="text-danger small mt-2">{fieldErrorMessage('groups')}</div>
                )}
            </div>

            {/* Sección de datos profesionales para abogados */}
            {isProfile && isLawyer() && (
                <div style={{ ...sectionStyle, border: '2px solid #bbdefb' }}>
                    <div style={sectionHeaderStyle}>
                        <div style={{ ...iconContainerStyle, backgroundColor: '#e8eaf6' }}>
                            <i className="fas fa-balance-scale" style={{ color: '#5c6bc0', fontSize: '16px' }}></i>
                        </div>
                        <div>
                            <h6 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Datos Profesionales</h6>
                            <small className="text-muted">Información para documentos legales (poderes simples)</small>
                        </div>
                    </div>
                    
                    <div 
                        className="p-3 rounded mb-3" 
                        style={{ 
                            backgroundColor: '#f5f5f5', 
                            border: '1px dashed #bdbdbd' 
                        }}
                    >
                        <TextAreaField
                            label="Dirección profesional para documentos legales"
                            value={userForm.lawyer_full_address || ''}
                            onChange={(value) => {
                                setUserForm({ ...userForm, lawyer_full_address: value });
                                onFocusRemove('lawyer_full_address');
                            }}
                            onFocus={() => onFocusRemove('lawyer_full_address')}
                            placeholder="Ej: Av. Providencia 1234, Oficina 567, Providencia, Santiago"
                            error={fieldErrorMessage('lawyer_full_address')}
                            rows={2}
                        />
                        <div 
                            className="mt-3 p-2 rounded"
                            style={{ backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}
                        >
                            <small style={{ color: '#1565c0' }}>
                                <i className="fas fa-info-circle me-2"></i>
                                <strong>Uso:</strong> Esta dirección aparecerá en los poderes simples como: 
                                <em>&ldquo;...con domicilio para estos efectos en [tu dirección]...&rdquo;</em>
                            </small>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserForm;
